import { useEffect, useState } from 'react'
import { myLanes } from '../lib/support'
import { Link } from 'react-router-dom'
import type { Me } from '../lib/me'
import { can, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { supabase } from '../lib/supabase'
import { messageOf } from '../lib/errors'
import { useHubLang } from '../lib/i18n'
import { GUIDE_LINKS, STARS } from '../lib/hubCopy'
import { GuideLink } from '../components/guides/GuideLink'
import { AppShell } from '../components/AppShell'
import { Dialog } from '../components/Dialog'
import { GiveForm } from '../components/GiveRecognition'
import { RecognitionCard } from '../components/RecognitionCard'
import { ShopFloor } from '../components/shop/ShopFloor'
import { MyOrders } from '../components/shop/MyOrders'
import {
  loadMyStars, loadFeaturedEndorsements, loadWall, loadStarSeries,
  type Endorsement, type MyStars, type StarBucket, type WallItem,
} from '../lib/stars'
import { onStarsEarned } from '../lib/starPulse'
import { StarsGivenWidget, StarsReceivedWidget } from '../components/StarOneWidget'
import { StarOneAgreement } from '../components/StarOneAgreement'
import { useStarOne } from '../lib/terms'

/**
 * Star One — #/stars. REC-7, REC-21, with REC-2's picker and REC-3's compose.
 *
 * The fifth route, and the one that ended App.tsx's router argument (OPS-35).
 *
 * ── THE WALL IS THE SCREEN; THE FORM IS A DIALOG ────────────────────────────
 *
 * REC-21. It used to stack balances, then the give form, then the wall, so the
 * thing you came to read started below a form you had not asked for. Now it is
 * the hub's own two-column layout — the same `.hub__cols` / `.hub__main` /
 * `.hub__side` that already handles the 1100px collapse and SHL-6's scrolling
 * rail — with the wall as the main column and the form behind a button (REC-20).
 *
 * ── NO RANKING. NOT A FILTER THAT COULD BE SWITCHED ON LATER ────────────────
 *
 * D10, upheld by B22 on 14 Sep rather than reversed: asked for a monthly
 * leaderboard, David declined all three rankings and chose badges instead —
 * earned on the SHAPE of somebody's giving, not the amount (REC-23). So there is
 * no "top recipients", no sort control and no per-person total anywhere on this
 * screen, and `recognition_wall()` offers no sort argument to pass even if
 * somebody added a control here. When the trophy feed lands on this rail it
 * must stay a set of thresholds: the moment anything counts or orders badges
 * per person, the league table is back through a third door.
 *
 * The honest caveat stands: every recognition is public with its count on it,
 * so a determined person can tally it in a spreadsheet. The position is that we
 * decline to compute the ranking for people.
 *
 * ── YOUR NUMBERS ARE YOURS ──────────────────────────────────────────────────
 *
 * `my_stars()` is self-only with no person argument and no admin variant. This
 * screen shows the reader their own two figures and nobody else's — not their
 * manager's view, not a team roll-up. A per-person received total handed to
 * somebody's manager is performance data about how visible a person is, which
 * correlates with shift and site, and roughly 40% of this company is not in
 * front of a screen.
 *
 * ── TWO TABS, AND THEY ARE REAL ROUTES ──────────────────────────────────────
 *
 * Wall and Store (David, 14 Sep). They are `#/stars` and `#/stars/store`
 * rather than a piece of component state, which buys three things for one line
 * of routing: the Back button works, the Store is linkable — REC-22's rail
 * widget has somewhere to point, and SHL-13 says a widget with a screen behind
 * it always links to it — and the wall's read does not run for somebody who
 * came to spend. `#/admin` / `#/admin/shop` is the same shape and the
 * precedent.
 *
 * ── A TAB IS NOT A CHIP AND IT IS NOT A MODESWITCH ──────────────────────────
 *
 * The app has two control vocabularies for switching things and neither fits,
 * so this adds a third rather than stretching one: a `.chip` is a FILTER, and
 * filters are many-of-N and live over a list they narrow — the wall had one
 * until 30 Sep, and making the section switch look identical to it would have
 * said the two do the same kind of thing. A `.modeswitch` carries an
 * armed/not-armed claim with tones, and its `danger` tone means an
 * irreversible control is now reachable; a tab claims nothing.
 *
 * `.tab` is therefore its own small recipe, sharing the control height scale
 * and the focus ring so it is not a third look as well as a third meaning. If a
 * second tabbed screen ever appears, this is the component it uses.
 *
 * ── THE ADMIN TAB MOVED OUT (David, 9 Oct) ──────────────────────────────────
 *
 * There was a third tab, Admin (`#/stars/admin`, REC-47 / 099, 23 Sep), for
 * `stars.admin` only, on the argument that the earn rates are "about the star
 * economy". It is now Star One admin under Administration
 * (routes/AdminStars.tsx, `#/admin/stars`): setting rates and tidying
 * endorsement words is administration, and Star One is the Wall and the Store
 * for everybody. `#/stars/admin` redirects there.
 */
export type StarsTab = 'wall' | 'store'

/**
 * 165 (David, 11 Oct): Star One asks for its terms "before they see any star
 * activity". Until the reader joins, #/stars and #/stars/store are the
 * agreement and nothing else — no wall, no balance, no Store — and none of the
 * star reads below run. Joining refreshes StarOneContext and this re-renders
 * as the page it always was.
 */
export function Stars({ me, tab = 'wall' }: { me: Me; tab?: StarsTab }) {
  const starOne = useStarOne()
  if (starOne.status === 'agreed') return <StarsJoined me={me} tab={tab} />
  return (
    <AppShell
      current="Star One"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {starOne.status === 'needed' && (
        <div className="staragree__wrap">
          <StarOneAgreement variant="page" />
        </div>
      )}
    </AppShell>
  )
}

function StarsJoined({ me, tab = 'wall' }: { me: Me; tab?: StarsTab }) {
  const lang = useHubLang()
  const t = STARS[lang]

  const [stars, setStars] = useState<MyStars | null>(null)
  // 128: the featured endorsements — the give form's preset chips.
  const [featured, setFeatured] = useState<Endorsement[]>([])
  const [wall, setWall] = useState<WallItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [giving, setGiving] = useState(false)
  const [orderNotice, setOrderNotice] = useState<string | null>(null)
  const [ordersVersion, setOrdersVersion] = useState(0)
  const [series, setSeries] = useState<StarBucket[]>([])

  useEffect(() => {
    let live = true
    Promise.all([loadMyStars(), loadFeaturedEndorsements()])
      .then(([s, f]) => {
        if (!live) return
        setStars(s)
        setFeatured(f)
      })
      .catch((e: Error) => live && setError(messageOf(e)))
    // The received tile's twelve-month chart — the hub's read, for the hub's
    // card (13 Oct) — and again when the pill says something landed. Its own
    // catch: a chart that will not load is a missing chart, not a broken page.
    const loadSeries = () =>
      loadStarSeries('12m')
        .then((b) => live && setSeries(b))
        .catch(() => live && setSeries([]))
    loadSeries()
    const off = onStarsEarned(() => {
      loadSeries()
      loadMyStars().then((s) => live && setStars(s)).catch(() => {})
    })
    return () => {
      live = false
      off()
    }
  }, [])

  // The wall re-reads when the tab comes back to it. (The endorsement filter
  // that used to drive this too went on 30 Sep.) Guarded on the tab: somebody who came to spend does not need a page of the
  // wall fetched behind the screen they are looking at. The dependency is the
  // tab and not a ref, so returning to the wall re-reads it — which is right,
  // because they may have given somebody stars in between.
  useEffect(() => {
    if (tab !== 'wall') return
    let live = true
    setWall(null)
    loadWall()
      .then((w) => live && setWall(w))
      .catch((e: Error) => live && setError(messageOf(e)))
    return () => {
      live = false
    }
  }, [tab])

  // After a give: re-read both. Not an optimistic splice — give_recognition
  // debits the allowance, credits a balance and writes three content rows in
  // one transaction, and 028c is this project's worked example of that shape
  // reporting success while writing nothing. The screen shows what the
  // database holds.
  async function afterGive() {
    const [s, w] = await Promise.all([loadMyStars(), loadWall()])
    setStars(s)
    setWall(w)
  }

  // An order moves the earned balance and nothing on the wall, so this re-reads
  // the one thing that changed. `my_stars()` also settles any matured hold on
  // the way past (063), which is why it is a call and not a subtraction.
  async function afterOrder() {
    setStars(await loadMyStars())
  }

  // A placed order: the balance, the order list, and the sentence the closed
  // dialog would have said. See ShopFloor's `reload`.
  async function afterPlaced(notice: string) {
    setOrderNotice(notice)
    setOrdersVersion((v) => v + 1)
    await afterOrder()
  }

  return (
    <AppShell
      current="Star One"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {error && <p className="composer__error" role="alert">{error}</p>}

      {/* 353 (HLP-50): one link for the page, sparingly - above the tabs. */}
      <p className="stars__guide">
        <GuideLink guide="getting-started" section="star-one" label={GUIDE_LINKS[lang].starOne} />
      </p>
      <nav className="tabs" aria-label={t.sections}>
        <Link
          to="/stars"
          className={`tab${tab === 'wall' ? ' tab--on' : ''}`}
          aria-current={tab === 'wall' ? 'page' : undefined}
        >
          {t.wall}
        </Link>
        <Link
          to="/stars/store"
          className={`tab${tab === 'store' ? ' tab--on' : ''}`}
          aria-current={tab === 'store' ? 'page' : undefined}
        >
          {t.store}
        </Link>
      </nav>

      <div className="hub__cols">
        <div className="hub__main">
          {tab === 'store' ? (
            <>
              {/* YOUR ORDERS FIRST (David and Chandra, 25 Sep). It was below a
                  grid of every item, so what you had already bought — and the
                  proof an order had landed — was the last thing on the page.
                  MyOrders renders nothing for somebody with no orders, so the
                  grid is still the first thing a new shopper sees. */}
              {orderNotice && (
                <p className="stars__done" role="status">{orderNotice}</p>
              )}
              <MyOrders onChanged={afterOrder} version={ordersVersion} />
              <ShopFloor
                balanceAvailable={stars?.balanceAvailable ?? 0}
                onOrdered={afterPlaced}
              />
            </>
          ) : (
          <section className="stars__wall">
            <div className="stars__wallhead">
              {/* David, 14 Sep. "Recognition wall" named the furniture; this
                  names what the reader is looking at a page of. It is the
                  heading over EVERYBODY's recognitions — the you is the company,
                  not the reader — which is why it stays on the wall and the
                  rail cards say "Stars you received" instead. */}
              <h2 className="card__title">{t.youreAStar}</h2>
              {/* NO FILTER ROW (David, 30 Sep). The endorsement chips that
                  narrowed the wall are gone: the wall is everybody's
                  recognitions, newest first, and nothing else. The server
                  still accepts p_endorsements (128) for whoever needs it
                  next — the client simply does not send it. */}
            </div>

            {wall === null ? (
              <p className="empty" aria-busy="true">{t.loadingWall}</p>
            ) : wall.length === 0 ? (
              <p className="empty">{t.nobodyYet}</p>
            ) : (
              <ul className="stars__list">
                {wall.map((r) => (
                  <RecognitionCard
                    key={r.contentId}
                    item={r}
                    featured={featured}
                    onChanged={afterGive}
                  />
                ))}
              </ul>
            )}
          </section>
          )}
        </div>

        {/* THE HUB'S TWO STAR TILES, NOT A CARD OF ITS OWN (David, 13 Oct).
            This rail was one tall "Your stars" card — both balances, the last
            three credits, the button. It is now the same StarsGivenWidget and
            StarsReceivedWidget the hub rail carries, in the same `.hub__hooks`
            rail, so below 1100px they are the same swipe row ABOVE the wall
            that the hub has above its feed, and the wall starts on the first
            screen. One component per tile across both pages means the two
            currencies are drawn one way everywhere (REC-8's rule: give and
            spend never share a card or a total). The last three credits are
            behind the received tile's "See all", as on the hub (11 Oct). */}
        <aside className="hub__side hub__hooks" aria-label={t.yourStars}>
          <StarsGivenWidget stars={stars} onGive={() => setGiving(true)} />
          <StarsReceivedWidget stars={stars} series={series} locale={me.locale} />
        </aside>
      </div>

      {/* REC-20. The form is a dialog, so it costs the screen nothing when
          nobody is writing one. The state lives here rather than inside the
          Dialog so that "is the form open" is one fact the route owns. */}
      <Dialog open={giving} title={t.recognizeColleague} onClose={() => setGiving(false)}>
        <GiveForm
          featured={featured}
          allowanceRemaining={stars?.allowanceRemaining ?? 0}
          onGiven={afterGive}
          onDone={() => setGiving(false)}
        />
      </Dialog>
    </AppShell>
  )
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
}
